import { useState } from 'react';

const statusColors = {
  'Active': '#c9a84c',
  'Ongoing': '#2d6a4f',
  'Research': '#2F5496',
  'Completed': '#888',
  'Won': '#1a1a2e',
  'Passed': '#c0392b',
};

const typeColors = {
  'Tax Deed': '#1a1a2e',
  'Tax Lien': '#2d6a4f',
};

const emptyForm = {
  state: '', county: '', type: 'Tax Deed', platform: '',
  auctionDate: '', registrationDeadline: '', depositDeadline: '',
  depositAmount: '', status: 'Research', notes: '', url: ''
};

function AuctionTracker({ auctions = [], loading, onSave, onUpdate, onDelete, isGuest }) {
  const [filter, setFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const statuses = ['All', 'Active', 'Ongoing', 'Research', 'Completed', 'Won', 'Passed'];
  const filtered = filter === 'All' ? auctions : auctions.filter(a => a.status === filter);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const handleSubmit = async () => {
    if (!form.state || !form.county) return;
    setSaving(true);
    if (editingId) {
      await onUpdate(editingId, form);
    } else {
      await onSave(form);
    }
    setSaving(false);
    resetForm();
    setShowForm(false);
  };

  const handleEdit = (auction) => {
    setForm(auction);
    setEditingId(auction.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this auction?')) await onDelete(id);
  };

  const inputStyle = {
    width: '100%', padding: '8px', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '14px', marginTop: '4px',
    boxSizing: 'border-box'
  };
  const labelStyle = { fontSize: '12px', color: '#555', fontWeight: 'bold' };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
        Loading auctions...
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>Auction Tracker</h2>
        {!isGuest && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{ backgroundColor: '#1a1a2e', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
          >
            + Add
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', overflowX: 'auto', paddingBottom: '4px' }}>
        {statuses.map(s => (
          <button key={s} onClick={() => setFilter(s)} style={{
            padding: '6px 12px', borderRadius: '20px', border: 'none', whiteSpace: 'nowrap',
            backgroundColor: filter === s ? '#1a1a2e' : '#f0f0f0',
            color: filter === s ? 'white' : '#333',
            cursor: 'pointer', fontSize: '12px', fontWeight: filter === s ? 'bold' : 'normal',
            flexShrink: 0
          }}>
            {s} ({s === 'All' ? auctions.length : auctions.filter(a => a.status === s).length})
          </button>
        ))}
      </div>

      {auctions.length === 0 && (
        <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '40px 20px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>📅</div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', marginBottom: '6px' }}>No auctions yet</div>
          <div style={{ fontSize: '13px', color: '#888' }}>Click "+ Add" to track your first auction.</div>
        </div>
      )}

      {/* Auction Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {filtered.map(auction => (
          <div key={auction.id} style={{
            border: '1px solid #ddd', borderRadius: '8px',
            backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', overflow: 'hidden'
          }}>
            <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px' }}>{auction.county}</h3>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#888' }}>{auction.state}</p>
                </div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <span style={{ backgroundColor: typeColors[auction.type] || '#888', color: 'white', padding: '3px 7px', borderRadius: '4px', fontSize: '10px' }}>{auction.type}</span>
                  <span style={{ backgroundColor: statusColors[auction.status] || '#888', color: 'white', padding: '3px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>{auction.status}</span>
                </div>
              </div>
            </div>

            <div style={{ padding: '12px 14px' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <tbody>
                  {[
                    ['Platform', auction.platform],
                    ['Auction Date', auction.auctionDate],
                    ['Reg. Deadline', auction.registrationDeadline],
                    ['Deposit Deadline', auction.depositDeadline],
                    ['Deposit Amount', '$' + Number(auction.depositAmount || 0).toLocaleString()],
                  ].map(([label, value]) => (
                    <tr key={label}>
                      <td style={{ color: '#888', padding: '3px 0', width: '40%' }}>{label}</td>
                      <td style={{ fontWeight: label === 'Auction Date' || label === 'Deposit Amount' ? 'bold' : 'normal', color: label === 'Deposit Amount' ? '#c0392b' : '#333' }}>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {auction.notes && (
                <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#666', borderTop: '1px solid #eee', paddingTop: '8px' }}>
                  {auction.notes}
                </p>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                {!isGuest && (
                  <button onClick={() => handleEdit(auction)}
                    style={{ flex: 1, padding: '8px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Edit
                  </button>
                )}
                {auction.url && (
                  <button onClick={() => window.open('https://' + auction.url.replace(/^https?:\/\//, ''), '_blank')}
                    style={{ flex: 1, padding: '8px', backgroundColor: '#1a1a2e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                    Visit Site
                  </button>
                )}
                {!isGuest && (
                  <button onClick={() => handleDelete(auction.id)}
                    style={{ padding: '8px 12px', backgroundColor: '#fff0f0', border: '1px solid #ffcccc', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#c0392b' }}>
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '16px 16px 0 0', width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
            <h3 style={{ marginBottom: '16px', color: '#1a1a2e' }}>{editingId ? 'Edit Auction' : 'Add Auction'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { label: 'State', field: 'state' },
                { label: 'County', field: 'county' },
                { label: 'Platform', field: 'platform' },
                { label: 'Auction Date', field: 'auctionDate' },
                { label: 'Reg. Deadline', field: 'registrationDeadline' },
                { label: 'Deposit Deadline', field: 'depositDeadline' },
                { label: 'Deposit Amount ($)', field: 'depositAmount' },
                { label: 'Website URL', field: 'url' },
              ].map(({ label, field }) => (
                <div key={field}>
                  <label style={labelStyle}>{label}</label>
                  <input type="text" value={form[field]}
                    onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                    style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>Type</label>
                <select value={form.type} onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))} style={inputStyle}>
                  <option>Tax Deed</option>
                  <option>Tax Lien</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))} style={inputStyle}>
                  {statuses.filter(s => s !== 'All').map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea value={form.notes}
                  onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button onClick={handleSubmit} disabled={saving}
                style={{ flex: 1, padding: '12px', backgroundColor: saving ? '#888' : '#2d6a4f', color: 'white', border: 'none', borderRadius: '4px', cursor: saving ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Auction'}
              </button>
              <button onClick={() => { resetForm(); setShowForm(false); }}
                style={{ flex: 1, padding: '12px', backgroundColor: '#888', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuctionTracker;
